import React from 'react';
import { Popover, Typography, Space, Divider } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { DesignIcon, FulfillmentIcon, ShipmentIcon, TrackingIcon } from '../../../utils/icons';

const { Text, Title } = Typography;

const StatusOverviewTooltip = () => {
    const content = (
        <div style={{ maxWidth: 350, padding: '8px 0' }}>
            <Title level={5} style={{ margin: '0 0 12px 0', fontSize: 14 }}>
                Status Overview Guide
            </Title>
            
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
                This column shows a quick overview of 4 key statuses through colored boxes and the order status bar.
            </Text>

            {/* Status Boxes Section */}
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Status Boxes:
                </Text>
                
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 20, 
                            height: 20, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4
                        }}>
                            <DesignIcon style={{ fontSize: 10 }} />
                        </div>
                        <Text style={{ fontSize: 12 }}>
                            <Text strong>Customization Status</Text> - Design and mockup progress
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 20, 
                            height: 20, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4
                        }}>
                            <FulfillmentIcon style={{ fontSize: 10 }} />
                        </div>
                        <Text style={{ fontSize: 12 }}>
                            <Text strong>Fulfillment Status</Text> - Production and fulfillment
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 20, 
                            height: 20, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4
                        }}>
                            <ShipmentIcon style={{ fontSize: 10 }} />
                        </div>
                        <Text style={{ fontSize: 12 }}>
                            <Text strong>Confirm Shipment</Text> - Shipment confirmation status
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 20, 
                            height: 20, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4
                        }}>
                            <TrackingIcon style={{ fontSize: 10 }} />
                        </div>
                        <Text style={{ fontSize: 12 }}>
                            <Text strong>Shipment Status</Text> - Package tracking status
                        </Text>
                    </div>
                </Space>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Color Meanings Section */}
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Color Meanings:
                </Text>
                
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#d9d9d9',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Gray - Not started / No data</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#1890ff',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Blue - In progress / Active</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#fadb14',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Yellow - Review / Waiting</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#722ed1',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Purple - Ready / Confirming</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#52c41a',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Green - Completed / Confirmed</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                            width: 12, 
                            height: 12, 
                            backgroundColor: '#ff4d4f',
                            borderRadius: 2
                        }}></div>
                        <Text style={{ fontSize: 12 }}>Red - Error / Failed</Text>
                    </div>
                </Space>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Order Status Bar Section */}
            <div>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Order Status Bar:
                </Text>
                <Text style={{ fontSize: 12 }}>
                    The vertical bar on the right shows the overall order status with matching background colors.
                </Text>
            </div>
        </div>
    );

    return (
        <Popover 
            content={content} 
            title={null}
            trigger="click"
            placement="bottomLeft"
            overlayStyle={{ maxWidth: 400 }}
        >
            <QuestionCircleOutlined 
                style={{ 
                    marginLeft: 4, 
                    color: '#1890ff', 
                    cursor: 'pointer',
                    fontSize: 12
                }} 
            />
        </Popover>
    );
};

export default StatusOverviewTooltip;
